import { history } from 'duck/store'
import { TemporalEntityId } from 'entities-next/common'
import React, { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { LoadingScreen } from 'scenes/Dashboard/components-next/Loading'
import { EntityId } from 'scenes/Dashboard/data/common/types'
import { ExtendableStyles, Testable } from 'utils-next/types'

import {
  ApiCorporateData,
  CORPORATE_CACHE_STORE_ID,
  getApiCorporateHandlers,
} from '../../../../../../../../entities-next/corporate'
import { CORPORATE_MANAGER_PATH } from '../../../../CorporateManager.routes'
import { CorporateDataForm } from '../../components'
import { useCorporateDetailsViewStore } from '../../CorporateDetailsView.store'
import {
  failedCorporateOperationAction,
  successfulCorporateCreationAction,
  successfulCorporateUpdateAction,
} from './CorporateDetailsFormViewAlerts.component'

export type CorporateDetailsFormViewProps = ExtendableStyles & Testable

export function CorporateDetailsFormView({ className, style, ...otherProps }: CorporateDetailsFormViewProps) {
  const {
    state: { corporate, status, actionBarRef },
  } = useCorporateDetailsViewStore()
  const { updateOrCreateCorporate } = useCorporateDetailsFormViewHandlers()

  if (!corporate || status === 'loading') {
    return <LoadingScreen />
  }

  return (
    <CorporateDataForm
      corporate={corporate}
      onSubmit={updateOrCreateCorporate}
      actionBarRef={actionBarRef}
      {...otherProps}
    />
  )
}

function useCorporateDetailsFormViewHandlers() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const updateOrCreateCorporate = useCallback(
    async (corporateId: EntityId, corporateData: ApiCorporateData) => {
      const { apiCreateCorporate, apiUpdateCorporate } = getApiCorporateHandlers()

      if (corporateId === TemporalEntityId.new) {
        try {
          await apiCreateCorporate({ ...corporateData })
          dispatch(successfulCorporateCreationAction(corporateData))
          history.push(CORPORATE_MANAGER_PATH)
          queryClient.refetchQueries(CORPORATE_CACHE_STORE_ID)
        } catch (e) {
          if (e instanceof Error) {
            dispatch(failedCorporateOperationAction(new Error(`Failed creating Corporate: ${e.message}`)))
          } else {
            dispatch(failedCorporateOperationAction(new Error('Failed creating Corporate')))
          }
        }
      } else {
        try {
          await apiUpdateCorporate(corporateId, corporateData)
          dispatch(successfulCorporateUpdateAction(corporateData))
          history.push(CORPORATE_MANAGER_PATH)
          queryClient.refetchQueries(CORPORATE_CACHE_STORE_ID)
        } catch (e) {
          if (e instanceof Error) {
            dispatch(failedCorporateOperationAction(new Error(`Failed updating Corporate: ${e.message}`)))
          } else {
            dispatch(failedCorporateOperationAction(new Error('Failed updating Corporate')))
          }
        }
      }
    },
    [dispatch]
  )

  return {
    updateOrCreateCorporate,
  }
}
